import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import {
  List,
  ListChild,
  Option,
  StyledSearchInput,
} from "./AutoComplete.style";
import get from "lodash/get";
import isArray from "lodash/isArray";
import isNumber from "lodash/isNumber";
import clsx from "clsx";
import { Text } from "src/components/ui/Text";
import { BaseButton } from "src/components/ui/baseButton";
import { ArrowDown, AddIcon, MinusIcon, CheckMark } from "./icons";

export const VisibleRenderProps = ({ renderProps }) => {
  const [open, setOpen] = useState(false);

  const onToggle = useCallback(() => setOpen((previous) => !previous), []);

  return renderProps({ open, onToggle });
};

const AutoComplete = (props) => {
  const {
    name,
    control,
    groupOptions = {},
    optionExtendRender,
    optionLoading,
    onClickFromOption,
    defaultGroupOption,
    ignoreBlur,
    value,
    ...rest
  } = props;
  const ownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  /*
    
      Transform to option schema
  
    */

  const transformOptions = useMemo(() => {
    if (isArray(groupOptions)) {
      return groupOptions.map((option) => ({
        label: option.name,
        children: [],
        value: option.id,
      }));
    }
    return Object.keys(groupOptions).map((key) => {
      return {
        label: key,
        children: groupOptions[key].map((child) => ({
          label: child.name,
          value: child.id,
        })),
        value: null,
      };
    });
  }, [groupOptions]);

  useEffect(() => {
    if (transformOptions && ignoreBlur) {
      setFilterOptions(transformOptions);
    }
  }, [transformOptions]);

  /*
      
      Handle state open of dropdown
      
      */

  const handleOutside = useCallback((event) => {
    if (ownRef.current && !ownRef.current?.contains(event.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, []);

  const handleChildChange = useCallback(
    (callback) => {
      setOpen(!open);
      callback();
    },
    [setOpen, open]
  );

  const handleOpen = useCallback(() => {
    setFilterOptions(transformOptions);
    setOpen(!open);
  }, [open, transformOptions]);

  const handleSearch = useCallback(
    (event, callback) => {
      const { value } = event.target;
      setOpen(true);
      callback(value);

      if (isArray(groupOptions)) {
        const filteredOptions = transformOptions.filter((option) =>
          option.label.toLowerCase().includes(value.toLowerCase())
        );
        setFilterOptions(filteredOptions);
      } else {
        const filteredOptions = transformOptions.reduce((acc, current) => {
          const childrenFiltered = current.children?.filter((child) =>
            child.label.toLowerCase().includes(value.toLowerCase())
          );
          acc.push({
            label: current.label,
            children: current.label.toLowerCase().includes(value.toLowerCase())
              ? current.children
              : childrenFiltered,
          });
          return acc;
        }, []);

        setFilterOptions(filteredOptions);
      }
    },
    [transformOptions]
  );

  const onInputBlur = (v, onChange) => {
    if (ignoreBlur) {
      return;
    }
    if (!isNumber(v)) {
      onChange("");
    }
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState: { errors } }) => {
        const isGroupOptionsArray = isArray(groupOptions);
        const parent = isGroupOptionsArray
          ? transformOptions.find((parent) =>
              value ? parent.value === value : parent.value === field.value
            )
          : transformOptions.find((parent) =>
              parent.children.find((child) =>
                value ? child.value === value : child.value === field.value
              )
            );
        const targetChild = parent?.children?.find(
          (child) => child.value === field.value
        );
        const initialOptions = defaultGroupOption?.find(
          (child) => child.value === field.value
        );
        const errorMsg = get(errors, `${name}.message`, undefined);

        return (
          <div ref={ownRef} className="inline-grid w-full">
            <StyledSearchInput
              {...field}
              {...rest}
              value={
                get(
                  isGroupOptionsArray
                    ? parent
                    : targetChild
                    ? targetChild
                    : initialOptions,
                  "label"
                ) || field.value
              }
              onBlur={() => onInputBlur(field.value, field.onChange)}
              type="text"
              onClick={props?.disabled ? () => {} : handleOpen}
              onChange={(e) => handleSearch(e, field.onChange)}
              className={clsx({
                "border-vekin-message-error": Boolean(errorMsg),
              })}
            />
            <div className="absolute right-0 h-full rounded ">
              <BaseButton
                variant="tertiary"
                size="small"
                className={`absolute right-0 top-[0.625rem] mr-1 bg-transparent shadow-none ${clsx(
                  {
                    "opacity-0 !cursor-not-allowed": props?.disabled,
                  }
                )}`}
                onClick={props?.disabled ? () => {} : handleOpen}
              >
                <img
                  src={ArrowDown}
                  alt="dropdown lists"
                  className={`duration-75 ${clsx({
                    "rotate-180": open,
                    "opacity-0": props?.disabled,
                  })}`}
                />
              </BaseButton>
            </div>

            <List
              style={{
                opacity: open ? "1" : "0",
                visibility: open ? "visible" : "hidden",
              }}
            >
              {typeof optionExtendRender === "function" && optionExtendRender()}

              {optionLoading ? (
                <Text
                  variant="medium-14"
                  className="text-center text-vekin-gray10"
                >
                  Loading...
                </Text>
              ) : !filterOptions.length ? (
                <Text
                  variant="medium-14"
                  className="text-center text-vekin-gray10"
                >
                  Not Found
                </Text>
              ) : null}

              {filterOptions.map((option) => (
                <VisibleRenderProps
                  key={option.label}
                  renderProps={({ open: isChildOpen, onToggle }) => {
                    return (
                      <Option className="relative">
                        <Text
                          data-theme="main"
                          variant="semi-12"
                          className={`flex items-center gap-2 py-1 leading-4 cursor-pointer select-none text-primary hover:bg-vekin-green-50/50 ${clsx(
                            {
                              "bg-vekin-green-50 text-vekin":
                                option.value === field.value,
                              "hover:bg-vekin-green-50/50 text-primary":
                                option.value !== field.value,
                            }
                          )}`}
                          onClick={
                            option.children.length
                              ? onToggle
                              : () => {
                                  handleChildChange(() =>
                                    field.onChange(option.value)
                                  );
                                  if (typeof onClickFromOption === "function") {
                                    onClickFromOption(option.value);
                                  }
                                }
                          }
                        >
                          {option.children.length ? (
                            isChildOpen ? (
                              <div>
                                <img
                                  src={MinusIcon}
                                  alt="minus icon"
                                  className="fill-vekin-gray10 min-w-[10px]"
                                />
                              </div>
                            ) : (
                              <div>
                                <img
                                  src={AddIcon}
                                  alt="minus icon"
                                  className="fill-vekin-gray10 min-w-[10px]"
                                />
                              </div>
                            )
                          ) : null}
                          {option.label}
                        </Text>
                        {option.children && isChildOpen && (
                          <ListChild>
                            {option.children.map((child) => (
                              <Option key={child.value} className="relative">
                                <Text
                                  data-theme="main"
                                  variant="semi-12"
                                  className={`flex items-center gap-2 leading-4 cursor-pointer select-none  py-1 rounded ${clsx(
                                    {
                                      "bg-vekin-green-50 text-vekin":
                                        child.value === field.value,
                                      "hover:bg-vekin-green-50/50 text-primary":
                                        child.value !== field.value,
                                    }
                                  )}`}
                                  onClick={() =>
                                    handleChildChange(() =>
                                      field.onChange(child.value)
                                    )
                                  }
                                >
                                  {child.label}
                                </Text>

                                {child.value === field.value && (
                                  <div className="absolute right-3 top-2">
                                    <img src={CheckMark} alt="check icon" />
                                  </div>
                                )}
                              </Option>
                            ))}
                          </ListChild>
                        )}

                        {/*when is active option label */}
                        {option.value === field.value && (
                          <div className="absolute right-3 top-[0.375rem]">
                            <img src={CheckMark} alt="check icon" />
                          </div>
                        )}
                      </Option>
                    );
                  }}
                />
              ))}
            </List>

            {errorMsg ? (
              <label className="label !py-1 text-vekin-message-error">
                <span className="label-text-alt text-vekin-message-error">
                  {errorMsg}
                </span>
              </label>
            ) : null}
          </div>
        );
      }}
    />
  );
};

export default AutoComplete;
